import { useState, useEffect } from 'react';
import { getTodayPacific, addDays, formatDateISOPacific } from '../../../../lib/datetime';

interface DateFilterProps {
  from?: string;
  to?: string;
  onChange: (from: string | undefined, to: string | undefined) => void;
}

// Quick filter presets
const QUICK_FILTERS = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'This month', days: -1 }, // Special case
];

export function DateFilter({ from, to, onChange }: DateFilterProps) {
  const [localFrom, setLocalFrom] = useState(from ?? '');
  const [localTo, setLocalTo] = useState(to ?? '');

  // Sync with props
  useEffect(() => {
    setLocalFrom(from ?? '');
  }, [from]);

  useEffect(() => {
    setLocalTo(to ?? '');
  }, [to]);

  const handleBlur = () => {
    onChange(localFrom || undefined, localTo || undefined);
  };

  const applyQuickFilter = (days: number) => {
    // Use Pacific timezone for "today" calculations
    const todayStr = getTodayPacific();

    if (days === -1) {
      // This month - calculate first of month in Pacific timezone
      const today = new Date();
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const fromDate = formatDateISOPacific(firstOfMonth);
      setLocalFrom(fromDate);
      setLocalTo(todayStr);
      onChange(fromDate, todayStr);
    } else if (days === 0) {
      // Today
      setLocalFrom(todayStr);
      setLocalTo(todayStr);
      onChange(todayStr, todayStr);
    } else {
      // Last N days
      const fromDate = addDays(todayStr, -days);
      setLocalFrom(fromDate);
      setLocalTo(todayStr);
      onChange(fromDate, todayStr);
    }
  };

  return (
    <div className="space-y-3">
      {/* Quick filters */}
      <div className="flex flex-wrap gap-1">
        {QUICK_FILTERS.map((qf) => (
          <button
            key={qf.label}
            onClick={() => applyQuickFilter(qf.days)}
            className="px-2 py-1 text-xs bg-sage-100 text-sage-700 rounded hover:bg-sage-200 transition-colors"
          >
            {qf.label}
          </button>
        ))}
      </div>

      {/* Date inputs */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <label className="block text-xs text-sage-500 mb-1">From</label>
          <input
            type="date"
            value={localFrom}
            onChange={(e) => setLocalFrom(e.target.value)}
            onBlur={handleBlur}
            className="w-full px-3 py-2 text-sm border border-sage-200 rounded-lg focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-sage-500 mb-1">To</label>
          <input
            type="date"
            value={localTo}
            onChange={(e) => setLocalTo(e.target.value)}
            onBlur={handleBlur}
            className="w-full px-3 py-2 text-sm border border-sage-200 rounded-lg focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500"
          />
        </div>
      </div>
    </div>
  );
}
